import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { IndianRupee, CalendarDays, Clock, Users, Cake, Heart, MessageCircle, Send, AlertTriangle, Timer, Landmark, CreditCard, CalendarClock, Phone, ChevronDown, ChevronUp, EyeOff } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, formatApiErrorDetail } from "../../lib/api";
import { openWhatsApp } from "../../lib/whatsapp";
import { useMode } from "../../context/ModeContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { StatusBadge, inputCls } from "./shared";

const todayStr = () => new Date().toLocaleDateString("en-CA");
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

const PRESETS = [["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["year", "This Year"], ["all", "All Time"]];

const rangeFor = (preset) => {
  const now = new Date();
  const d = (x) => x.toLocaleDateString("en-CA");
  if (preset === "today") return [todayStr(), todayStr()];
  if (preset === "week") { const s = new Date(now); s.setDate(now.getDate() - ((now.getDay() + 6) % 7)); return [d(s), todayStr()]; }
  if (preset === "month") return [monthStart(), todayStr()];
  if (preset === "year") return [`${now.getFullYear()}-01-01`, todayStr()];
  return [null, null];
};

const EXPIRY_LABEL = { expired: ["EXPIRED", "bg-red-100 text-red-600"], "30d": ["≤ 30 days", "bg-[#C5A059]/20 text-[#8a6d33]"], "60d": ["≤ 60 days", "bg-blue-100 text-blue-700"] };
const TICK_X = { fontSize: 11, fill: "#5C6A63" };
const TICK_Y = { fontSize: 12, fill: "#5C6A63" };

export default function Overview() {
  const { viewMode } = useMode();
  const masked = viewMode === "manager";
  const [stats, setStats] = useState(null);
  const [celebs, setCelebs] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [range, setRange] = useState({ preset: "month", from: monthStart(), to: todayStr() });
  const [modal, setModal] = useState(null);
  const [alertsOpen, setAlertsOpen] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadStats = useCallback(() => {
    setLoadError(false);
    const params = range.from && range.to ? { from_date: range.from, to_date: range.to } : {};
    api.get("/dashboard/stats", { params })
      .then(({ data }) => setStats(data))
      .catch(() => setLoadError(true));
  }, [range.from, range.to]);
  const loadAlerts = useCallback(() => api.get("/alerts").then(({ data }) => setAlerts(data)).catch(() => {}), []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    loadAlerts();
    api.get("/celebrations").then(({ data }) => setCelebs(data)).catch(() => {});
  }, [loadAlerts]);

  const setPreset = (preset) => {
    const [from, to] = rangeFor(preset);
    setRange({ preset, from, to });
  };

  const autoSendGreeting = async (ev) => {
    try {
      await api.post("/celebrations/send", { customer_id: ev.customer_id, occasion: ev.occasion });
      toast.success(`${ev.occasion === "birthday" ? "Birthday" : "Anniversary"} greeting sent to ${ev.name} via WhatsApp API`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const shareReorder = () => {
    const lines = alerts.low_stock.map((p, i) => `${i + 1}. ${p.name}${p.brand ? ` (${p.brand})` : ""} — left: ${p.stock} ${p.unit}, order: ${p.suggested_qty} ${p.unit}`);
    const msg = `🛒 *GREEN Professionals Salon — Reorder List*\n\n${lines.join("\n")}\n\nPlease confirm availability and price. Thank you!`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const expiryAction = async (row, action) => {
    try {
      await api.post(`/products/${row.id}/expiry-action`, { action, qty: row.stock });
      toast.success(action === "backbar" ? `${row.name} moved to backbar use` : `${row.name} marked returned to distributor`);
      loadAlerts(); loadStats();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const clearCheque = async (c) => {
    try {
      await api.patch(`/expenses/${c.expense_id}/cheque`, { cheque_status: "cleared" });
      toast.success(`Cheque ${c.cheque_no ? "#" + c.cheque_no : ""} marked cleared`);
      loadAlerts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!stats) {
    if (loadError) {
      return (
        <div className="bg-white border border-[#E5E7E5] rounded-md p-8 text-center max-w-md" data-testid="overview-load-error">
          <p className="text-sm font-semibold text-[#1C1C1C]">Couldn't load the dashboard</p>
          <p className="text-xs text-[#5C6A63] mt-1">Check your connection, or your session may need a fresh PIN unlock.</p>
          <button onClick={loadStats} data-testid="overview-retry-btn"
            className="mt-4 bg-[#0A3F2F] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#0d5540]">
            Retry
          </button>
        </div>
      );
    }
    return <div className="text-[#5C6A63] text-sm" data-testid="overview-loading">Loading…</div>;
  }

  const p = stats.profit;
  const otherCards = [
    { label: "Today's Appointments", value: stats.today_appointments, icon: CalendarDays, sub: "scheduled for today" },
    { label: "Pending Requests", value: stats.pending_count, icon: Clock, sub: "awaiting confirmation" },
    { label: "Total Customers", value: stats.total_customers, icon: Users, sub: "in your CRM" },
  ];

  const alertBadges = alerts ? [
    { key: "reorder", icon: AlertTriangle, label: "Low Stock / Reorder", count: alerts.counts.low_stock, color: "text-red-600 bg-red-50 border-red-200" },
    { key: "expiry", icon: Timer, label: "Expiry Watch", count: alerts.counts.expiry, color: "text-[#8a6d33] bg-[#C5A059]/10 border-[#C5A059]/40" },
    { key: "cheques", icon: Landmark, label: "Cheques (PDC) Due", count: alerts.counts.cheques_due, color: "text-blue-700 bg-blue-50 border-blue-200" },
    { key: "cards", icon: CreditCard, label: "Card Dues", count: alerts.counts.cards_due, color: "text-purple-700 bg-purple-50 border-purple-200" },
    { key: "scheduled", icon: CalendarClock, label: "Upcoming Payables", count: alerts.counts.scheduled, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  ] : [];
  const totalAlerts = alerts ? Object.values(alerts.counts).reduce((s, n) => s + n, 0) : 0;

  return (
    <div className="space-y-6" data-testid="overview-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1C1C1C]">Overview</h1>
        <p className="text-[#5C6A63] text-sm mt-1">Your salon at a glance.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="overview-date-bar">
        {PRESETS.map(([k, label]) => (
          <button key={k} onClick={() => setPreset(k)} data-testid={`overview-preset-${k}`}
            className={`text-xs font-semibold px-3.5 py-2 rounded-full ${range.preset === k ? "bg-[#C5A059] text-[#1C1C1C]" : "bg-white border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F]"}`}>
            {label}
          </button>
        ))}
        <input data-testid="overview-from-input" type="date" className={`${inputCls} !w-36`} value={range.from || ""}
          onChange={(e) => setRange({ preset: "custom", from: e.target.value, to: range.to || todayStr() })} />
        <span className="text-xs text-[#5C6A63]">to</span>
        <input data-testid="overview-to-input" type="date" className={`${inputCls} !w-36`} value={range.to || ""}
          onChange={(e) => setRange({ preset: "custom", from: range.from || monthStart(), to: e.target.value })} />
      </div>

      {alerts && totalAlerts > 0 && (
        <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm" data-testid="alerts-hub">
          <button onClick={() => setAlertsOpen(!alertsOpen)} data-testid="alerts-toggle-btn"
            className="w-full flex items-center justify-between px-5 py-3.5 text-left">
            <span className="text-sm font-bold text-[#1C1C1C] flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" /> Critical Action Center
              <span className="text-[11px] font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full" data-testid="alerts-total-count">{totalAlerts} alerts</span>
            </span>
            {alertsOpen ? <ChevronUp className="w-4 h-4 text-[#5C6A63]" /> : <ChevronDown className="w-4 h-4 text-[#5C6A63]" />}
          </button>
          {alertsOpen && (
            <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {alertBadges.map((b) => (
                <button key={b.key} onClick={() => b.count > 0 && setModal(b.key)} data-testid={`alert-badge-${b.key}`}
                  className={`border rounded-md p-3 text-left transition-transform ${b.count > 0 ? `${b.color} hover:scale-[1.02] cursor-pointer` : "bg-gray-50 border-gray-100 text-gray-400"}`}>
                  <div className="flex items-center justify-between">
                    <b.icon className="w-4 h-4" />
                    <span className="text-xl font-bold" data-testid={`alert-count-${b.key}`}>{b.count}</span>
                  </div>
                  <p className="text-[11px] font-semibold mt-1.5">{b.label}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {masked ? (
          <div className="bg-[#0A3F2F] text-white rounded-md p-5 shadow-sm flex flex-col justify-between" data-testid="masked-revenue-card">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider text-white/60 font-semibold">Net Profit &amp; Savings</p>
              <EyeOff className="w-4 h-4 text-[#C5A059]" />
            </div>
            <p className="text-3xl font-bold text-[#C5A059] tracking-[0.2em]" data-testid="masked-revenue-value">••••••</p>
            <p className="text-[11px] text-white/50 mt-2">Confidential — CEO Mode only</p>
          </div>
        ) : p ? (
          <div className="md:col-span-2 lg:col-span-1 lg:row-span-1 bg-[#0A3F2F] text-white rounded-md p-5 shadow-sm" data-testid="profit-card">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider text-white/60 font-semibold">Net Profit &amp; Savings</p>
              <IndianRupee className="w-4 h-4 text-[#C5A059]" />
            </div>
            <p className="text-3xl font-bold text-[#C5A059]" data-testid="net-profit-value">{fmt(p.net_profit)}</p>
            <div className="mt-3 space-y-1 text-[11px]">
              <div className="flex justify-between text-white/80"><span>Gross Revenue</span><span data-testid="profit-gross">{fmt(p.gross_revenue)}</span></div>
              <div className="flex justify-between text-white/80"><span>Less: Operational Expenses</span><span data-testid="profit-opex">-{fmt(p.operational_expenses)}</span></div>
              <div className="flex justify-between text-white/80"><span>Less: Products Consumed</span><span data-testid="profit-cogs">-{fmt(p.products_consumed)}</span></div>
              {(p.interest_income > 0 || p.interest_expense > 0) && (
                <div className="flex justify-between text-white/80"><span>Interest (net)</span><span>{fmt(p.interest_income - p.interest_expense)}</span></div>
              )}
              <div className="border-t border-white/20 pt-1 flex justify-between font-bold"><span>= Net Business Profit</span><span>{fmt(p.net_profit)}</span></div>
              <div className="flex justify-between text-white/80"><span>Less: Personal Drawings</span><span data-testid="profit-drawings">-{fmt(p.personal_drawings)}</span></div>
              <div className="border-t border-white/20 pt-1.5 flex justify-between items-center">
                <span className="font-bold">Net Retained Savings</span>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${p.net_savings >= 0 ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`} data-testid="net-savings-value">{fmt(p.net_savings)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm" data-testid="stat-card-total-revenue">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-wider text-[#5C6A63] font-semibold">Total Revenue</p>
              <IndianRupee className="w-4 h-4 text-[#C5A059]" />
            </div>
            <p className="text-3xl font-bold text-[#0A3F2F]">{fmt(stats.total_revenue)}</p>
            <p className="text-xs text-[#5C6A63] mt-1">services + bills + packages in range</p>
          </div>
        )}
        {otherCards.map((c) => (
          <div key={c.label} data-testid={`stat-card-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-wider text-[#5C6A63] font-semibold">{c.label}</p>
              <c.icon className="w-4 h-4 text-[#C5A059]" />
            </div>
            <p className="text-3xl font-bold text-[#0A3F2F]">{c.value}</p>
            <p className="text-xs text-[#5C6A63] mt-1">{c.sub}</p>
          </div>
        ))}
      </div>

      {celebs && celebs.events.length > 0 && (
        <div className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm" data-testid="celebrations-widget">
          <h2 className="text-sm font-bold text-[#1C1C1C] mb-1">🎉 Celebrations — next 7 days</h2>
          <p className="text-xs text-[#5C6A63] mb-4">Send a greeting with your special offer. Edit the message texts in Settings.</p>
          <div className="space-y-3">
            {celebs.events.map((ev) => (
              <div key={`${ev.customer_id}-${ev.occasion}`} className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E5E7E5] pb-3 last:border-0" data-testid={`celebration-row-${ev.customer_id}-${ev.occasion}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {ev.occasion === "birthday"
                    ? <Cake className="w-4 h-4 text-[#C5A059] shrink-0" />
                    : <Heart className="w-4 h-4 text-rose-500 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1C1C1C] truncate">{ev.name}</p>
                    <p className="text-xs text-[#5C6A63]">{ev.occasion === "birthday" ? "Birthday" : "Anniversary"} · {ev.days_away === 0 ? "🎊 Today!" : ev.days_away === 1 ? "Tomorrow" : `in ${ev.days_away} days`} ({ev.date})</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openWhatsApp(ev.phone, celebs.greetings[ev.occasion].replace("{name}", ev.name))}
                    data-testid={`greet-whatsapp-${ev.customer_id}-${ev.occasion}`}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-emerald-600/40 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </button>
                  {celebs.wa_configured && (
                    <button onClick={() => autoSendGreeting(ev)} data-testid={`greet-autosend-${ev.customer_id}-${ev.occasion}`}
                      className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#0A3F2F] text-white hover:bg-[#0d5540] transition-colors">
                      <Send className="w-3.5 h-3.5" /> Auto-send
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-4">
        {!masked && (
        <div className="lg:col-span-3 bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm">
          <h2 className="text-sm font-bold text-[#1C1C1C] mb-4">Revenue — {range.preset === "all" ? "Last 7 Days" : "Selected Period"}</h2>
          <div className="h-64" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%" minHeight={220}>
              <AreaChart data={stats.revenue_by_day || []}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0A3F2F" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0A3F2F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7E5" />
                <XAxis dataKey="day" tick={TICK_X} />
                <YAxis tick={TICK_Y} />
                <Tooltip formatter={(v) => [fmt(v), "Revenue"]} />
                <Area type="monotone" dataKey="revenue" stroke="#0A3F2F" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        )}
        <div className={`${masked ? "lg:col-span-5" : "lg:col-span-2"} bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm`}>
          <h2 className="text-sm font-bold text-[#1C1C1C] mb-4">Recent Appointments</h2>
          <div className="space-y-3" data-testid="recent-appointments">
            {(stats.recent_appointments || []).length === 0 && <p className="text-sm text-[#5C6A63]">No appointments yet.</p>}
            {(stats.recent_appointments || []).map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 border-b border-[#E5E7E5] pb-3 last:border-0">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1C1C1C] truncate">{a.customer_name}</p>
                  {a.phone ? (
                    <span className="flex items-center gap-1.5">
                      <a href={`tel:${a.phone}`} data-testid={`recent-phone-${a.id}`} className="text-xs font-semibold text-[#0A3F2F] hover:underline flex items-center gap-1">
                        <Phone className="w-3 h-3" />{a.phone}
                      </a>
                      <button onClick={() => openWhatsApp(a.phone, `Hi ${a.customer_name}, confirming your appointment for ${a.service_name} at GREEN Professionals Salon.`)}
                        data-testid={`recent-wa-${a.id}`} title="WhatsApp" className="text-emerald-600 hover:text-emerald-700">
                        <MessageCircle className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ) : (
                    <p className="text-xs text-[#5C6A63] italic">No phone provided</p>
                  )}
                  <p className="text-xs text-[#5C6A63] truncate">{a.service_name} · {a.date} {a.time}</p>
                </div>
                <StatusBadge status={a.status} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={modal === "reorder"} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>🚨 Products to Reorder</DialogTitle></DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                  <th className="px-3 py-2 font-semibold">Product</th>
                  <th className="px-3 py-2 font-semibold">Brand</th>
                  <th className="px-3 py-2 font-semibold">Shelf Stock</th>
                  <th className="px-3 py-2 font-semibold">Alert Level</th>
                  <th className="px-3 py-2 font-semibold">Order Qty</th>
                </tr>
              </thead>
              <tbody data-testid="reorder-table-body">
                {alerts?.low_stock.map((r) => (
                  <tr key={r.id} className="border-b border-[#E5E7E5] last:border-0" data-testid={`reorder-row-${r.id}`}>
                    <td className="px-3 py-2 font-semibold">{r.name}</td>
                    <td className="px-3 py-2 text-[#5C6A63]">{r.brand || "—"}</td>
                    <td className="px-3 py-2 font-bold text-red-600">{r.stock} {r.unit}</td>
                    <td className="px-3 py-2">{r.low_stock_level} {r.unit}</td>
                    <td className="px-3 py-2 font-semibold text-[#0A3F2F]">{r.suggested_qty} {r.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={shareReorder} data-testid="share-reorder-whatsapp-btn"
            className="w-full bg-emerald-600 text-white font-semibold py-2.5 rounded-full text-sm hover:bg-emerald-700 flex items-center justify-center gap-2">
            <MessageCircle className="w-4 h-4" /> Share Order via WhatsApp
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={modal === "expiry"} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>⚠️ Product Expiry Watch</DialogTitle></DialogHeader>
          <div className="space-y-2" data-testid="expiry-list">
            {alerts?.expiry.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border border-[#E5E7E5] rounded-md px-3 py-2.5" data-testid={`expiry-row-${r.id}`}>
                <div>
                  <p className="text-sm font-semibold">{r.name} {r.batch_no && <span className="text-xs text-[#5C6A63]">· Batch {r.batch_no}</span>}</p>
                  <p className="text-xs text-[#5C6A63]">{r.stock} {r.unit} in stock · expires {r.expiry}
                    <span className={`ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${EXPIRY_LABEL[r.status][1]}`}>{EXPIRY_LABEL[r.status][0]}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => expiryAction(r, "backbar")} data-testid={`expiry-backbar-${r.id}`}
                    className="text-[11px] font-bold px-3 py-1.5 rounded-full bg-[#0A3F2F] text-white hover:bg-[#0d5540]">Use in Backbar</button>
                  <button onClick={() => expiryAction(r, "returned")} data-testid={`expiry-return-${r.id}`}
                    className="text-[11px] font-bold px-3 py-1.5 rounded-full border border-red-300 text-red-600 hover:bg-red-50">Return to Distributor</button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={modal === "cheques"} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>🏦 Cheques (PDC) Due</DialogTitle></DialogHeader>
          <p className="text-xs text-[#5C6A63]">Verify sufficient bank balance before the cheque date.</p>
          <div className="space-y-2" data-testid="cheque-alerts-list">
            {alerts?.cheques_due.map((c) => (
              <div key={c.expense_id} className={`flex items-center justify-between gap-2 border rounded-md px-3 py-2.5 ${c.overdue ? "border-red-300 bg-red-50" : "border-[#E5E7E5]"}`} data-testid={`cheque-alert-${c.expense_id}`}>
                <div>
                  <p className="text-sm font-semibold">Cheque {c.cheque_no ? `#${c.cheque_no}` : ""} · {fmt(c.amount)}</p>
                  <p className="text-xs text-[#5C6A63]">{c.payee || "—"} · due {c.cheque_date}{c.overdue && <span className="ml-1 text-red-600 font-bold">OVERDUE</span>}</p>
                </div>
                <button onClick={() => clearCheque(c)} data-testid={`cheque-mark-cleared-${c.expense_id}`}
                  className="text-[11px] font-bold px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700">Mark as Cleared</button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={modal === "cards"} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>💳 Credit Card Dues</DialogTitle></DialogHeader>
          <div className="space-y-2" data-testid="card-alerts-list">
            {alerts?.cards_due.map((c) => (
              <div key={c.id} className="border border-purple-200 bg-purple-50 rounded-md px-3 py-2.5" data-testid={`card-alert-${c.id}`}>
                <p className="text-sm font-semibold text-purple-800">{c.name}</p>
                <p className="text-xs text-purple-700">Payable {fmt(c.balance)} · due {c.next_due} ({c.days_to_due <= 0 ? "today / overdue" : `in ${c.days_to_due} day${c.days_to_due > 1 ? "s" : ""}`})</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#5C6A63]">Record card bill payments in Finance → Credit Cards &amp; Cheques.</p>
        </DialogContent>
      </Dialog>

      <Dialog open={modal === "scheduled"} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>📅 Upcoming Payables — next 7 days</DialogTitle></DialogHeader>
          {alerts?.scheduled.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#5C6A63] mb-2">Scheduled expenses</p>
              <div className="space-y-2" data-testid="scheduled-list">
                {alerts.scheduled.map((s) => (
                  <div key={s.expense_id} className="flex items-center justify-between border border-[#E5E7E5] rounded-md px-3 py-2" data-testid={`scheduled-row-${s.expense_id}`}>
                    <div>
                      <p className="text-sm font-semibold">{s.category}</p>
                      <p className="text-xs text-[#5C6A63]">{s.description || "—"} · due {s.scheduled_date}</p>
                    </div>
                    <span className="text-sm font-bold text-red-600">{fmt(s.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {alerts?.vendor_dues.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#5C6A63] mb-2 mt-2">Distributor / vendor dues</p>
              <div className="space-y-2" data-testid="vendor-dues-list">
                {alerts.vendor_dues.map((v) => (
                  <div key={v.supplier_id} className="flex items-center justify-between border border-[#E5E7E5] rounded-md px-3 py-2" data-testid={`vendor-due-${v.supplier_id}`}>
                    <div>
                      <p className="text-sm font-semibold">{v.name}</p>
                      <p className="text-xs text-[#5C6A63]">last purchase {v.last_purchase}</p>
                    </div>
                    <span className="text-sm font-bold text-red-600">{fmt(v.balance_due)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
